import HistoryRounded from '@mui/icons-material/HistoryRounded';
import SupportAgentRounded from '@mui/icons-material/SupportAgentRounded';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Skeleton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, unwrap, type Schemas } from '../../../api/client';
import { ConfirmDialog } from '../../../components/ConfirmDialog';
import { dateTime, relativeTime } from '../../../lib/format';
import { errorMessage, useNotify } from '../../../lib/notify';
import { useShop } from '../../../shop/ShopProvider';
import { SectionCard } from '../ui';

export type SupportGrant = Schemas['SupportGrant'];

export const SUPPORT_HOURS = [1, 4, 24, 72] as const;
const supportKey = ['support-access'] as const;

export function useSupportGrants() {
  return useQuery({
    queryKey: supportKey,
    queryFn: () => unwrap(api.GET('/support-access')),
  });
}

function useGrantSupport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { hours: number; note?: string }) =>
      unwrap(api.POST('/support-access', { body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: supportKey }),
  });
}

function useRevokeSupport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(api.POST('/support-access/{id}/revoke', { params: { path: { id } } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: supportKey }),
  });
}

/** "2 h 15 min", "3 days" — time left on a grant. */
export function timeLeft(expiresAt: string, now = Date.now()): string {
  const minutes = Math.max(0, Math.round((new Date(expiresAt).getTime() - now) / 60_000));
  if (minutes < 60) return `${String(minutes)} min`;
  const hours = Math.floor(minutes / 60);
  if (hours >= 48) return `${String(Math.round(hours / 24))} days`;
  const rest = minutes % 60;
  return rest ? `${String(hours)} h ${String(rest)} min` : `${String(hours)} h`;
}

const STATUS_CHIP: Record<SupportGrant['status'], { label: string; color: 'success' | 'default' }> =
  {
    ACTIVE: { label: 'Active', color: 'success' },
    EXPIRED: { label: 'Expired', color: 'default' },
    REVOKED: { label: 'Ended', color: 'default' },
  };

/**
 * Settings → Support access: the shop lets platform support look at its data (read only) for a
 * limited time, and can end it at any moment. Shown to `settings.manage` only.
 */
export function SupportTab() {
  const { format } = useShop();
  const notify = useNotify();
  const grants = useSupportGrants();
  const grant = useGrantSupport();
  const revoke = useRevokeSupport();
  const [hours, setHours] = useState<number>(4);
  const [note, setNote] = useState('');
  const [confirmRevoke, setConfirmRevoke] = useState<SupportGrant | null>(null);

  const list = grants.data?.data ?? [];
  const active = list.find((g) => g.status === 'ACTIVE');
  const history = list.filter((g) => g !== active);

  const submit = async () => {
    try {
      const trimmed = note.trim();
      await grant.mutateAsync({ hours, ...(trimmed ? { note: trimmed } : {}) });
      setNote('');
      notify.success(`Support can view your shop for ${String(hours)} h`);
    } catch (e) {
      notify.error(e);
    }
  };

  return (
    <Stack spacing={2.5}>
      <SectionCard
        icon={<SupportAgentRounded />}
        title="Support access"
        description="Let our support team look at your shop to help with a problem. They can only view, never change anything, and every visit is recorded in your audit log."
      >
        {grants.isLoading ? (
          <Skeleton variant="rounded" height={120} />
        ) : grants.error ? (
          <Alert severity="error">{errorMessage(grants.error)}</Alert>
        ) : (
          <Stack spacing={2.5}>
            {active ? (
              <Alert
                severity="success"
                variant="outlined"
                action={
                  <Button color="inherit" size="small" onClick={() => setConfirmRevoke(active)}>
                    End access
                  </Button>
                }
              >
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  Support can view your shop — expires in {timeLeft(active.expiresAt)}
                </Typography>
                <Typography variant="caption" color="text.secondary" component="div">
                  Until {dateTime(active.expiresAt, format)} · granted by {active.grantedBy.name}
                  {active.note ? ` · “${active.note}”` : ''}
                </Typography>
              </Alert>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Support has no access to your shop right now.
              </Typography>
            )}
            <Divider />
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {active ? 'Replace with a new grant' : 'Give access for'}
              </Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={hours}
                onChange={(_, v: number | null) => v !== null && setHours(v)}
                aria-label="Access duration"
              >
                {SUPPORT_HOURS.map((h) => (
                  <ToggleButton key={h} value={h} sx={{ px: 2, fontWeight: 600 }}>
                    {h === 72 ? '3 days' : `${String(h)} h`}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
            <TextField
              label="Note for support (optional)"
              placeholder="e.g. Invoice totals look wrong since Monday"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              slotProps={{ htmlInput: { maxLength: 500 } }}
              multiline
              minRows={2}
            />
            <Box>
              <Button
                variant="contained"
                onClick={() => void submit()}
                disabled={grant.isPending}
                startIcon={<SupportAgentRounded />}
              >
                Grant access
              </Button>
            </Box>
          </Stack>
        )}
      </SectionCard>

      <SectionCard
        icon={<HistoryRounded />}
        title="History"
        description="Earlier support access for this shop."
      >
        {history.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No earlier support access.
          </Typography>
        ) : (
          <Stack divider={<Divider flexItem />} spacing={1.25} aria-label="Support access history">
            {history.map((g) => (
              <Stack
                key={g.id}
                direction={{ xs: 'column', sm: 'row' }}
                spacing={{ xs: 0.5, sm: 2 }}
                sx={{ alignItems: { sm: 'center' } }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {dateTime(g.createdAt, format)} → {dateTime(g.revokedAt ?? g.expiresAt, format)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" component="div" noWrap>
                    Granted by {g.grantedBy.name}
                    {g.revokedBy ? ` · ended by ${g.revokedBy.name}` : ''}
                    {g.note ? ` · “${g.note}”` : ''}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {relativeTime(g.createdAt)}
                </Typography>
                <Chip
                  size="small"
                  label={STATUS_CHIP[g.status].label}
                  color={STATUS_CHIP[g.status].color}
                  sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
                />
              </Stack>
            ))}
          </Stack>
        )}
      </SectionCard>

      <ConfirmDialog
        open={confirmRevoke !== null}
        title="End support access?"
        body="Support will no longer be able to view your shop. You can grant access again at any time."
        confirmLabel="End access"
        danger
        onClose={() => setConfirmRevoke(null)}
        onConfirm={async () => {
          if (!confirmRevoke) return;
          try {
            await revoke.mutateAsync(confirmRevoke.id);
            notify.success('Support access ended');
            setConfirmRevoke(null);
          } catch (e) {
            notify.error(e);
          }
        }}
      />
    </Stack>
  );
}
